import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Swal from "sweetalert2";
import { useDispatch } from "react-redux";
import MainLayout from "../../components/layout/MainLayout";
import Breadcrumbs from "../../components/common/Breadcrumbs";
import IndividualClientFileForm from "./IndividualClientFileForm";
import { createIndividualClientFile } from "../../store/slices/individualClientFileSlice";
import { fetchPatient } from "../../store/slices/patientsSlice";

// Pre-defined graduated transition activities from the standard form.
const defaultTransitionActivities = [
  "Supervised Outings (community, shopping, recreation)",
  "Family Visits (on-site / therapeutic)",
  "Home Visits (client returns home temporarily)",
  "Overnight / Extended Home Leave",
  "Vocational / Educational Site Visit",
].map((activity, i) => ({
  id: Date.now() + i,
  activity,
  initiated_date: "",
  frequency: "",
  accompanied_by: "",
  activity_status: "",
}));

const AddIndividualClientFile = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { state } = useLocation();
  const { caseId, patientId } = state || {};

  const [formData, setFormData] = useState({
    patient_id: patientId || "",
    case_id: caseId || "",
    goals: [
      { id: Date.now() + 1, intend_to: "", priority_rank: "", target_date: "", goal_achievement: "", smart: false },
    ],
    progress_notes: [
      { id: Date.now() + 2, note_date: "", symptoms: "", functional_progress: "", goal_achievement: "", participation: "", barriers: "", adjustments_plan: "" },
      { id: Date.now() + 3, note_date: "", symptoms: "", functional_progress: "", goal_achievement: "", participation: "", barriers: "", adjustments_plan: "" },
    ],
    family_meetings: [
      { id: Date.now() + 4, meeting_date: "", attendees: "", key_concerns: "", action_points: "" },
    ],
    transition_activities: defaultTransitionActivities,
  });

  const [patientData, setPatientData] = useState(null);

  useEffect(() => {
    if (patientId) {
      dispatch(fetchPatient(patientId))
        .unwrap()
        .then((patient) => setPatientData(patient))
        .catch((err) => console.error("Failed to fetch patient details:", err));
    }
  }, [dispatch, patientId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const res = await dispatch(createIndividualClientFile(formData)).unwrap();
      Swal.fire({
        title: "Success!",
        text: "Individual Client File added successfully.",
        icon: "success",
        timer: 2000,
        showConfirmButton: false,
      });
      navigate(`/phase03/individual-client-file/edit/${res.id}`, {
        state: { patientId: patientId || res.patient_id, caseId: caseId || res.case_id },
      });
    } catch (err) {
      let errorMessage = "Failed to add Individual Client File";
      if (typeof err === "string") {
        errorMessage = err;
      } else if (typeof err === "object" && err !== null) {
        errorMessage = Object.values(err).flat().join("\n");
      }
      Swal.fire({ title: "Error", text: errorMessage, icon: "error" });
    }
  };

  return (
    <MainLayout>
      <div className="container-xxl flex-grow-1 container-p-y">
        <Breadcrumbs />

        {patientData && (
          <div className="card mb-4">
            <div className="card-body bg-light-primary rounded-4">
              <div className="row g-3">
                <div className="col-md-3">
                  <span className="fw-semibold text-secondary small d-block">Client Name:</span>
                  <span className="fw-bold text-dark">{patientData.first_name} {patientData.last_name}</span>
                </div>
                <div className="col-md-3">
                  <span className="fw-semibold text-secondary small d-block">MR Number:</span>
                  <span className="fw-bold text-dark">{patientData.mr_no || "N/A"}</span>
                </div>
                <div className="col-md-3">
                  <span className="fw-semibold text-secondary small d-block">Date of Birth:</span>
                  <span className="fw-bold text-dark">
                    {patientData.dob ? new Date(patientData.dob).toLocaleDateString("en-GB") : "N/A"}
                  </span>
                </div>
                <div className="col-md-3">
                  <span className="fw-semibold text-secondary small d-block">Gender:</span>
                  <span className="fw-bold text-dark capitalize">{patientData.gender || "N/A"}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="card">
          <div className="card-header border-bottom">
            <h5 className="mb-0">Add Individual Client File</h5>
            <small className="text-muted">Comprehensive Psychiatric Rehabilitation Framework (CARF-Aligned)</small>
          </div>
          <form onSubmit={handleSubmit}>
            <IndividualClientFileForm formData={formData} setFormData={setFormData} />
            <div className="row px-4 pb-4">
              <div className="col-md-2 ms-auto">
                <button type="submit" className="btn btn-primary w-100">
                  <i className="ri-save-3-line me-1"></i> Save
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </MainLayout>
  );
};

export default AddIndividualClientFile;
